import axios, { AxiosResponse } from 'axios'

import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { ResponseSuccess } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { ID } from '@models/base'
import { QueueModel } from '@models/queue'
import { QueueProcessModel } from '@models/queueProcess'

import { API_URL } from '@/constants/constant'

const QUEUES_URL = `${API_URL}/queues`

type ResponseQueues = Response<WithFilter<Array<QueueModel>>>
type ResponseQueue = Response<{ queue: QueueModel }>
type ResponseQueueProcess = Response<{ queue_process: QueueProcessModel }>

type ParamsGetQueue = {
  with_nodes?: boolean
}

type QueryOptionForQueueDisplay = RequestQuery & {
  queue_display_id?: ID
}

type PayloadCreateUpdateQueue = {
  name: string
  allow_multiple_process?: boolean
  description?: string
  is_active?: boolean
  is_ending?: boolean
  is_default?: boolean
  preferred_next_queue_id?: ID
  role_ids?: Array<ID>
}

type PayloadUpdateQueuePrinter = {
  clinic_printer_id: ID
}

type PayloadFilterQueues = RequestQuery<{
  is_active: boolean | null
  allow_multiple_process: boolean | null
  with_nodes?: boolean
}>

interface IQueuesAPI {
  filter: (query: PayloadFilterQueues) => Promise<ResponseQueues>

  get: (id: ID, params?: ParamsGetQueue) => Promise<ResponseQueue>

  create: (payload: PayloadCreateUpdateQueue) => Promise<ResponseQueue>

  update: (id: ID, payload: PayloadCreateUpdateQueue) => Promise<ResponseQueue>

  delete: (id: ID) => Promise<ResponseSuccess>

  process: () => Promise<ResponseQueueProcess>

  getOptions: (query: RequestQuery) => Promise<ResponseQueues>

  getOptionsForQueueCounterCheckInForm: () => Promise<ResponseQueues>

  getOptionsForQueueDisplayForm: (query: QueryOptionForQueueDisplay) => Promise<ResponseQueues>

  updateQueuePrinter: (queueId: ID, payload: PayloadUpdateQueuePrinter) => Promise<ResponseQueue>
}

export const QueuesAPI: IQueuesAPI = {
  filter: async (query) => {
    return axios
      .post(`${QUEUES_URL}/filter`, {
        ...query,
        with_nodes: false,
      })
      .then((r: AxiosResponse) => r.data)
  },

  get: async (id, params) => {
    return axios.get(`${QUEUES_URL}/${id}`, { params }).then((r: AxiosResponse) => r.data)
  },

  create: async (payload) => {
    return axios.post(QUEUES_URL, payload).then((r: AxiosResponse) => r.data)
  },

  update: async (id, payload) => {
    return axios.put(`${QUEUES_URL}/${id}`, payload).then((r: AxiosResponse) => r.data)
  },

  delete: async (id) => {
    return axios.delete(`${QUEUES_URL}/${id}`).then((r: AxiosResponse) => r.data)
  },

  process: async () => {
    return axios.get(`${QUEUES_URL}/process`).then((r: AxiosResponse) => r.data)
  },

  getOptions: async (query) => {
    return axios.post(`${QUEUES_URL}/options`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForQueueCounterCheckInForm: async () => {
    return axios.post(`${QUEUES_URL}/options/queue-counter-check-in-form`).then((r: AxiosResponse) => r.data)
  },

  getOptionsForQueueDisplayForm: async (query) => {
    return axios.post(`${QUEUES_URL}/options/queue-display`, query).then((r: AxiosResponse) => r.data)
  },

  updateQueuePrinter: async (queueId, payload) => {
    return axios.patch(`${QUEUES_URL}/${queueId}/printer`, payload).then((r: AxiosResponse) => r.data)
  },
}

export type { PayloadCreateUpdateQueue, PayloadUpdateQueuePrinter, PayloadFilterQueues }
